import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import React from 'react'
import { useDispatch } from 'react-redux'
import { closeModal } from 'src/navigation/Lnk'
import styled from 'styled-components'

import {
  flyoutChromelessBodyStyle,
  flyoutWidthOverride,
} from './RightFlyout.style'

export enum RightFlyoutBodyWidths {
  Action = 'action',
  Transaction = 'transaction',
}

interface RightFlyoutProps {
  className?: string
  width: RightFlyoutBodyWidths | number
  /** @deprecated no-op; kept for backward compatibility */
  id?: string
  onClose?: () => void
  /** @deprecated no-op; kept for backward compatibility */
  onScroll?: (e: React.UIEvent<HTMLElement>) => void
  /** @deprecated no-op; kept for backward compatibility */
  childRef?: (node: HTMLDivElement) => void
  /** @deprecated no-op; kept for backward compatibility */
  parentRef?: (node: HTMLDivElement) => void
  /** @deprecated no-op; BaseFlyout fills the overlay height */
  fullHeight?: boolean
  suppressCloseModalAction?: boolean
  /**
   * Legacy lighter-overlay control. Only its *presence* is honored, not the
   * numeric value: when supplied (and `flyoutVariant` is not given), it maps to
   * BaseFlyout's `focus-background` variant so the content behind the flyout
   * stays partially visible. BaseFlyout's overlay opacity is fixed per variant,
   * so a specific opacity number can no longer be applied. Prefer `flyoutVariant`
   * for new code.
   */
  occlusionOpacity?: number
  ariaLabel?: string
  flyoutVariant?: 'default' | 'focus-background'
}

// Default BaseFlyout width: spacing(145) = 4 × 145 = 580 px (matches Transaction)
const TRANSACTION_WIDTH_PX = 580
const ACTION_WIDTH_PX = 700

function getWidthOverridePx(
  width: RightFlyoutBodyWidths | number,
): number | undefined {
  if (width === RightFlyoutBodyWidths.Transaction) return undefined
  if (width === RightFlyoutBodyWidths.Action) return ACTION_WIDTH_PX
  if (typeof width === 'number' && width !== TRANSACTION_WIDTH_PX) return width
  return undefined
}

// The styled wrapper places its generated class on the portal container (via
// BaseFlyout className → portalClassName), allowing .BaseFlyout-Container and
// .BaseFlyout-Content to be targeted for width overrides. The chromeless body
// style is applied unconditionally to preserve legacy RightFlyout's edge-to-edge,
// padding-free layout (consumers supply their own header/close/padding).
const StyledBaseFlyout = styled(BaseFlyout)<{ $widthOverridePx?: number }>`
  ${flyoutChromelessBodyStyle}
  ${({ $widthOverridePx }) =>
    $widthOverridePx != null && flyoutWidthOverride($widthOverridePx)}
`

const RightFlyout: React.FC<React.PropsWithChildren<RightFlyoutProps>> = (
  props,
): React.ReactElement => {
  const {
    ariaLabel = 'flyout',
    width,
    suppressCloseModalAction,
    onClose,
    className,
    flyoutVariant,
    occlusionOpacity,
  } = props
  const dispatch = useDispatch()

  const handleClose = (): void => {
    if (!suppressCloseModalAction) {
      dispatch(closeModal())
    }
    onClose?.()
  }

  // Preserve the legacy occlusionOpacity behavior: a custom opacity meant a
  // lighter overlay, which maps to BaseFlyout's 'focus-background' variant.
  // An explicit flyoutVariant always wins.
  const variant =
    flyoutVariant ?? (occlusionOpacity != null ? 'focus-background' : undefined)

  return (
    <StyledBaseFlyout
      isOpen={true}
      onClose={handleClose}
      aria-label={ariaLabel}
      variant={variant}
      showCloseButton={false}
      contentId="flyoutContent"
      bodyId="childContainer"
      $widthOverridePx={getWidthOverridePx(width)}
      className={className}
    >
      {props.children}
    </StyledBaseFlyout>
  )
}

export default RightFlyout
